import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FileText, User, Plus, Eye, Save, Pencil, XCircle, DollarSign, Trash2, MessageSquare } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ConfirmDialog from '@/components/shared/ConfirmDialog';
import { toast } from 'sonner';
import { apiGet, apiPost, apiPut, apiDelete } from '@/lib/api';
import AuthenticatedLayout from '@/components/layout/AuthenticatedLayout';
import { PageHeader, SectionCard, EmptyState, StatusBadge, BackLink, ReadOnlyValue, RadioGroup } from '@/components/shared/DashboardUI';
import { formatPhoneNumber, parsePhoneNumber, formatZipCode, formatCurrency, parseCurrency, US_STATES, CASE_TYPES, INSURANCE_CARRIERS, LIABILITY_LIMITS, formatDateOnly, getCaseTypeLabel, getInsuranceCarrierLabel, getLiabilityLimitsLabel, TREATMENT_LOCATIONS, TREATMENTS_COMPLETED_OPTIONS, formatTreatmentLocations } from '@/lib/formatters';
import CompareOffers from '@/components/CompareOffers';
import ProfileTab from '@/components/shared/ProfileTab';
import '@/styles/authenticated.css';
import { DocumentUpload, DisclaimerModal } from '@/components/DocumentUpload';
import DocumentRequestsCard from '@/components/DocumentRequestsCard';
import MessagesPanel from '@/components/shared/MessagesPanel';
import MessagesInbox from '@/components/shared/MessagesInbox';
import PoliceReportNotice, { requiresPoliceReport } from '@/components/PoliceReportNotice';
import { ActivityTimelineCard } from '@/components/ActivityTimeline';
import LawFirmTypeahead from '@/components/LawFirmTypeahead';
import { validateApplicationFormats, computeClientCompletion, getMissingRequiredFields } from '@/lib/applicationValidation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NAV_ITEMS = [
  { id: 'applications', label: 'My Applications', icon: FileText },
  { id: 'messages', label: 'Messages', icon: MessageSquare },
  { id: 'profile', label: 'Profile', icon: User },
];

// Tabs that own a URL segment, so they can be bookmarked and reloaded.
// 'applications' is the bare /client route.
const CLIENT_TABS = ['messages', 'profile'];

// Required fields and the completion meter now live in
// `@/lib/applicationValidation` so the meter and the save gate cannot drift
// apart — see the note there on why a blank never blocks a save.

const ClientDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('applications');
  const [selectedApplicationId, setSelectedApplicationId] = useState(null);

  useEffect(() => {
    const pathParts = location.pathname.split('/');
    if (pathParts[2] === 'application' && pathParts[3]) {
      setSelectedApplicationId(pathParts[3]);
      setActiveTab('applications');
    } else if (pathParts[2] && CLIENT_TABS.includes(pathParts[2])) {
      setActiveTab(pathParts[2]);
      setSelectedApplicationId(null);
    } else {
      setSelectedApplicationId(null);
    }
  }, [location.pathname]);

  useEffect(() => { fetchUser(); }, []);

  // Show a friendly toast when a logged-in user got bounced from /apply-now —
  // prevents duplicate-application confusion (the root cause of "she submitted
  // twice and now has two applications").
  useEffect(() => {
    if (location?.state?.applyNowRedirect) {
      const timer = setTimeout(() => {
        toast.info("You're already signed in", {
          description: 'Use the "New Application" button below instead of the public Apply Now form to avoid creating a duplicate.',
          duration: 8000,
        });
      }, 250);
      window.history.replaceState({}, document.title);
      return () => clearTimeout(timer);
    }
  }, [location]);

  // Show a friendly welcome toast the FIRST time a referred client lands here after auto-linking
  useEffect(() => {
    let payload = null;
    try {
      const raw = sessionStorage.getItem('referral_welcome');
      if (!raw) return;
      payload = JSON.parse(raw);
    } catch { return; }
    if (!payload || !payload.applicationId) {
      sessionStorage.removeItem('referral_welcome');
      return;
    }
    // Defer slightly so dashboard mounts before the toast appears
    const timer = setTimeout(() => {
      const firstName = payload.firstName || '';
      const attorney = payload.attorneyName || 'your attorney';
      const firm = payload.lawFirmName ? ` at ${payload.lawFirmName}` : '';
      toast.success(
        firstName ? `Welcome, ${firstName}!` : 'Welcome to MyCaseLoan!',
        {
          description: `Your application from ${attorney}${firm} is ready. Continue your application to request funding.`,
          duration: 10000,
          action: {
            label: 'Continue Application',
            onClick: () => navigate(`/client/application/${payload.applicationId}`)
          }
        }
      );
      sessionStorage.removeItem('referral_welcome');
    }, 350);
    return () => clearTimeout(timer);
  }, [navigate]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const fetchUser = async () => {
    try {
      setUser(await apiGet('/users/me'));
    } catch (error) { console.error('Failed to fetch user:', error); }
  };

  const handleNavClick = (tab) => {
    setActiveTab(tab);
    setSelectedApplicationId(null);
    navigate(CLIENT_TABS.includes(tab) ? `/client/${tab}` : '/client');
  };

  const handleViewApplication = (appId) => { navigate(`/client/application/${appId}`); };
  const handleBackToList = () => { setSelectedApplicationId(null); navigate('/client'); };

  const pageTitles = { applications: 'My Applications', messages: 'Messages', profile: 'Profile' };

  return (
    <AuthenticatedLayout user={user} activeTab={activeTab} onTabChange={handleNavClick} navItems={NAV_ITEMS} role="client" pageTitle={pageTitles[activeTab]}>
      {activeTab === 'applications' && <ApplicationsTab selectedApplicationId={selectedApplicationId} onViewApplication={handleViewApplication} onBackToList={handleBackToList} currentUser={user} />}
      {activeTab === 'messages' && <MessagesInbox currentUser={user} />}
      {activeTab === 'profile' && <ProfileTab user={user} onUpdate={fetchUser} />}
    </AuthenticatedLayout>
  );
};

/* ================================================================
   Applications Tab
   ================================================================ */
const ApplicationsTab = ({ selectedApplicationId, onViewApplication, onBackToList, currentUser }) => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [viewingApplication, setViewingApplication] = useState(null);
  const [errors, setErrors] = useState({});
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancellingApp, setCancellingApp] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [pendingSubmitAppId, setPendingSubmitAppId] = useState(null);
  const [pendingSubmitType, setPendingSubmitType] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);

  const initialFormData = {
    firstName: '', lastName: '', address1: '', address2: '', city: '', state: '', zip: '',
    phoneNumber: '', dateOfBirth: '', ssn: '',
    caseType: '', caseTypeOtherText: '', incidentDate: '', incidentDateUnknown: false,
    requestedAmount: '', accidentState: '', hasPriorFunding: '', hasOfferOrSettled: '',
    insuranceCarrier: '', insuranceCarrierOther: '', liabilityLimits: '', hasUmCoverage: '',
    umLimits: '', umPolicyNumber: '', umClaimNumber: '',
    lawyerName: '', attorneyEmail: '', lawFirmName: '', lawFirmPhone: '', firm_id: '',
    supportStaffName: '', supportStaffEmail: '',  // legacy fields — kept in state for backwards-compat with old apps, but no client UI surfaces them anymore. Removed input fields per Team Members feature.
    injuriesDescription: '', hasStartedTreatment: '', treatmentLocations: [],
    treatmentsCompleted: '', surgicalRecommendations: '', hadPtOrChiro: '',
    hadSeriousInjections: '', brokeBones: '', hadSurgery: '',
    otherInjuriesTreatments: '', comments: '',
    childSupportLiens: '', inBankruptcy: ''
  };

  const [formData, setFormData] = useState(initialFormData);
  const [currentDraftAppId, setCurrentDraftAppId] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [lawyerSearchResults, setLawyerSearchResults] = useState([]);
  const [showLawyerDropdown, setShowLawyerDropdown] = useState(false);
  const [lawyerSearchField, setLawyerSearchField] = useState(null);
  const [lawyerAutoFilled, setLawyerAutoFilled] = useState(false);
  const [searchDebounceTimer, setSearchDebounceTimer] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const searchLawyers = async (query, field) => {
    if (query.length < 2) { setLawyerSearchResults([]); setShowLawyerDropdown(false); return; }
    try {
      const data = await apiGet(`/lawyers/search?q=${encodeURIComponent(query)}&limit=10`);
      setLawyerSearchResults(data);
      setShowLawyerDropdown(data.length > 0);
      setLawyerSearchField(field);
    } catch (error) { console.error('Failed to search lawyers:', error); }
  };

  const handleLawyerSearch = (value, field) => {
    updateField(field === 'name' ? 'lawyerName' : 'attorneyEmail', value);
    setLawyerAutoFilled(false);
    if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
    const timer = setTimeout(() => { searchLawyers(value, field); }, 300);
    setSearchDebounceTimer(timer);
  };

  const handleLawyerSelect = (lawyer) => {
    setFormData(prev => ({ ...prev, lawyerName: lawyer.fullName || '', attorneyEmail: lawyer.email || '', lawFirmName: lawyer.lawFirmName || '', lawFirmPhone: lawyer.lawFirmPhone || '', firm_id: lawyer.firm_id || '' }));
    setLawyerAutoFilled(true);
    setShowLawyerDropdown(false);
    setLawyerSearchResults([]);
    setErrors(prev => ({ ...prev, lawyerName: null, attorneyEmail: null, lawFirmName: null, lawFirmPhone: null }));
  };

  useEffect(() => { fetchApplications(); }, []);

  useEffect(() => {
    if (selectedApplicationId && applications.length > 0) {
      const app = applications.find(a => a.application_id === selectedApplicationId);
      if (app) {
        // For drafts, drop the user straight into the editable form so they
        // can continue filling it out / upload documents. We MUST NOT set
        // viewingApplication for drafts — that would render the read-only
        // ApplicationDetailView and hide the editable form. For everything else
        // (submitted, approved, funded, declined, cancelled) we show the
        // read-only viewing pane via viewingApplication.
        if (app.status === 'draft') {
          setViewingApplication(null);
          // Only seed formData from the server when we're not already editing
          // this same draft locally — otherwise in-progress local edits would
          // get clobbered by a background fetchApplications() refresh.
          setCurrentDraftAppId(prev => {
            if (prev !== app.application_id) {
              setFormData({ ...initialFormData, ...app });
            }
            return app.application_id;
          });
          setShowForm(true);
        } else {
          setViewingApplication(app);
          setFormData({ ...initialFormData, ...app });
          setShowForm(false);
        }
      }
    } else if (!selectedApplicationId) {
      // Only reset when there's truly no selected app in the URL. We do NOT
      // touch formData here because fetchApplications() runs frequently and
      // any unconditional reset would wipe an in-progress new application.
      setViewingApplication(null);
    }
  }, [selectedApplicationId, applications]);

  const fetchApplications = async () => {
    try {
      setApplications(await apiGet('/applications'));
    } catch (error) { console.error('Failed to fetch applications:', error); }
  };

  const validateZip = (zip) => /^\d{5}(-\d{4})?$/.test(zip);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.address1.trim()) newErrors.address1 = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state) newErrors.state = 'State is required';
    if (!formData.zip.trim()) newErrors.zip = 'Zip code is required';
    else if (!validateZip(formData.zip)) newErrors.zip = 'Invalid zip code format';
    if (!formData.phoneNumber || !formData.phoneNumber.trim()) newErrors.phoneNumber = 'Phone number is required';
    if (!formData.caseType) newErrors.caseType = 'Case type is required';
    if (formData.caseType === 'other' && !formData.caseTypeOtherText.trim()) newErrors.caseTypeOtherText = 'Please describe your case type';
    if (!formData.requestedAmount || parseFloat(formData.requestedAmount) <= 0) newErrors.requestedAmount = 'Please enter an amount';
    if (!formData.accidentState) newErrors.accidentState = 'Accident state is required';
    if (!formData.hasPriorFunding) newErrors.hasPriorFunding = 'This field is required';
    if (!formData.hasOfferOrSettled) newErrors.hasOfferOrSettled = 'This field is required';
    if (!formData.insuranceCarrier) newErrors.insuranceCarrier = 'Insurance carrier is required';
    if (formData.insuranceCarrier === 'other' && (!formData.insuranceCarrierOther || !formData.insuranceCarrierOther.trim())) newErrors.insuranceCarrierOther = 'Please enter the carrier name';
    if (!formData.hasUmCoverage) newErrors.hasUmCoverage = 'This field is required';
    if (formData.hasUmCoverage === 'yes' && !formData.umLimits) newErrors.umLimits = 'UM limits are required';
    if (!formData.attorneyEmail.trim()) newErrors.attorneyEmail = 'Attorney email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.attorneyEmail)) newErrors.attorneyEmail = 'Invalid email format';
    if (!formData.lawFirmName.trim()) newErrors.lawFirmName = 'Law firm name is required';
    if (!formData.lawFirmPhone.trim()) newErrors.lawFirmPhone = 'Law firm phone is required';
    if (!formData.injuriesDescription || !formData.injuriesDescription.trim()) newErrors.injuriesDescription = 'Please describe your injuries';
    if (!formData.hasStartedTreatment) newErrors.hasStartedTreatment = 'This field is required';
    if (formData.hasStartedTreatment === 'yes') {
      if (!formData.treatmentLocations || formData.treatmentLocations.length === 0) newErrors.treatmentLocations = 'Please select at least one location';
      if (!formData.treatmentsCompleted) newErrors.treatmentsCompleted = 'This field is required';
      if (!formData.surgicalRecommendations) newErrors.surgicalRecommendations = 'This field is required';
    }
    if (!formData.hadPtOrChiro) newErrors.hadPtOrChiro = 'This field is required';
    if (!formData.hadSeriousInjections) newErrors.hadSeriousInjections = 'This field is required';
    if (!formData.brokeBones) newErrors.brokeBones = 'This field is required';
    if (!formData.hadSurgery) newErrors.hadSurgery = 'This field is required';
    if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
    if (!formData.childSupportLiens) newErrors.childSupportLiens = 'This field is required';
    if (!formData.inBankruptcy) newErrors.inBankruptcy = 'This field is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // For drafts we don't enforce full validation — partial drafts are allowed.
    setSavingDraft(true);
    try {
      const isUpdate = !!currentDraftAppId;
      const path = isUpdate ? `/applications/${currentDraftAppId}` : '/applications';
      // Normalize: empty numeric strings → null/0 so Pydantic accepts the draft.
      const payload = {
        ...formData,
        requestedAmount: formData.requestedAmount === '' || formData.requestedAmount === null ? 0 : parseFloat(formData.requestedAmount) || 0,
      };
      const data = isUpdate ? await apiPut(path, payload) : await apiPost(path, payload);
      const appId = data.application_id || currentDraftAppId;
      if (!isUpdate && appId) {
        setCurrentDraftAppId(appId);
        // Promote the new draft to its own URL so refresh, share, or bookmark
        // resumes the same in-progress application. The URL-watching useEffect
        // will keep formData in sync without clobbering local edits.
        navigate(`/client/application/${appId}`, { replace: true });
      }
      toast.success(isUpdate ? 'Draft updated' : 'Draft saved — you can now upload supporting documents');
      fetchApplications();
    } catch (error) {
      toast.error(error.message || 'Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSubmitApplication = async (appId) => {
    setPendingSubmitAppId(appId);
    setPendingSubmitType('existing');
    setShowDisclaimer(true);
  };

  const doSubmitApplication = async (appId) => {
    try {
      await apiPost(`/applications/${appId}/submit`);
      toast.success('Application submitted for lawyer review'); fetchApplications();
    } catch (error) { toast.error(error.message || 'Failed to submit application'); }
  };

  const handleCancelApplication = async () => {
    if (!selectedApplication) return;
    setCancellingApp(true);
    const isDraft = selectedApplication.status === 'draft';
    try {
      // Drafts are hard-deleted; in-progress apps are soft-cancelled to preserve the audit trail.
      const path = isDraft
        ? `/applications/${selectedApplication.application_id}`
        : `/applications/${selectedApplication.application_id}/cancel`;
      if (isDraft) await apiDelete(path); else await apiPost(path);
      toast.success(isDraft ? 'Draft deleted' : 'Application canceled successfully');
      setShowCancelDialog(false); setSelectedApplication(null); setViewingApplication(null);
      // If the deleted/cancelled app was the one selected via URL, return to the list
      if (selectedApplicationId === selectedApplication.application_id) {
        navigate('/client', { replace: true });
      }
      fetchApplications();
      if (onBackToList) onBackToList();
    } catch (error) { toast.error(isDraft ? 'Failed to delete draft' : 'Failed to cancel application'); }
    finally { setCancellingApp(false); }
  };

  const canCancelApplication = (status) => ['draft', 'pending_lawyer_review', 'approved'].includes(status);

  const updateField = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) setErrors({ ...errors, [field]: null });
  };

  const handleSubmitToLawyer = async () => {
    if (!validateForm()) { toast.error('Please fill all required fields before submitting to lawyer'); return; }
    setPendingSubmitType('new');
    setShowDisclaimer(true);
  };

  const doSubmitToLawyer = async () => {
    try {
      let appId = currentDraftAppId;
      // If we already have a draft, update it then submit. Otherwise create + submit.
      if (appId) {
        await apiPut(`/applications/${appId}`, formData);
      } else {
        const data = await apiPost('/applications', formData);
        appId = data.application_id;
      }
      await apiPost(`/applications/${appId}/submit`);
      toast.success('Application submitted for lawyer review!');
      setShowForm(false);
      setFormData(initialFormData);
      setErrors({});
      setCurrentDraftAppId(null);
      fetchApplications();
    } catch (error) { toast.error(error.message || 'Failed to submit application'); }
  };

  const handleDisclaimerAgree = async () => {
    setShowDisclaimer(false);
    if (pendingSubmitType === 'existing' && pendingSubmitAppId) {
      await doSubmitApplication(pendingSubmitAppId);
    } else if (pendingSubmitType === 'new') {
      await doSubmitToLawyer();
    }
    setPendingSubmitAppId(null);
    setPendingSubmitType(null);
  };

  return (
    <div data-testid="applications-tab">
      {showDisclaimer && (
        <DisclaimerModal applicationId={pendingSubmitAppId || 'new'} onAgree={handleDisclaimerAgree} onCancel={() => { setShowDisclaimer(false); setPendingSubmitAppId(null); setPendingSubmitType(null); }} getAuthHeaders={getAuthHeaders} />
      )}
      {viewingApplication ? (
        <ApplicationDetailView
          currentUser={currentUser}
          application={viewingApplication}
          onBack={() => onBackToList ? onBackToList() : setViewingApplication(null)}
          onUpdate={() => { fetchApplications(); if (onBackToList) onBackToList(); else setViewingApplication(null); }}
          userRole="client"
          onCancel={() => { setSelectedApplication(viewingApplication); setShowCancelDialog(true); }}
          canCancel={canCancelApplication(viewingApplication?.status)}
        />
      ) : (
        <>
          <PageHeader title="My Applications" subtitle="Track and manage your funding applications" actions={
            <button
              className="mcl-btn mcl-btn-primary"
              onClick={() => {
                // Always start from a fresh state when toggling the new-application form.
                // Without this, leftover formData from a previously viewed application
                // would clone into the new draft (e.g., back → New Application bug).
                const opening = !showForm;
                setCurrentDraftAppId(null);
                setFormData(initialFormData);
                setErrors({});
                setShowForm(opening);
                // When closing, also clear any draft selection from the URL so the
                // URL-watching effect doesn't immediately re-open the same draft.
                if (!opening && selectedApplicationId) {
                  navigate('/client');
                }
              }}
              data-testid="new-application-button"
            >
              <Plus size={16} /> {showForm ? 'Close Form' : 'New Application'}
            </button>
          } />

          {showForm && (
            <SectionCard title="New Funding Application" className="mb-4" data-testid="application-form">
              <form onSubmit={handleSubmit}>
                {/* Section 1: My Information */}
                <div className="mcl-card mb-3">
                  <div className="mcl-card-header"><h3 className="mcl-card-header-title">1. My Information</h3></div>
                  <div className="mcl-card-body">
                    <div className="mcl-form-row">
                      <div className="mcl-form-group">
                        <label className="mcl-form-label">First Name *</label>
                        <input className={`mcl-form-input ${errors.firstName ? 'is-invalid' : ''}`} value={formData.firstName} onChange={(e) => updateField('firstName', e.target.value)} data-testid="input-first-name" placeholder="Enter your first name" />
                        {errors.firstName && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.firstName}</div>}
                      </div>
                      <div className="mcl-form-group">
                        <label className="mcl-form-label">Last Name *</label>
                        <input className={`mcl-form-input ${errors.lastName ? 'is-invalid' : ''}`} value={formData.lastName} onChange={(e) => updateField('lastName', e.target.value)} data-testid="input-last-name" placeholder="Enter your last name" />
                        {errors.lastName && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.lastName}</div>}
                      </div>
                    </div>
                    <div className="mcl-form-group">
                      <label className="mcl-form-label">Address 1 *</label>
                      <input className={`mcl-form-input ${errors.address1 ? 'is-invalid' : ''}`} value={formData.address1} onChange={(e) => updateField('address1', e.target.value)} data-testid="input-address1" placeholder="Street address" />
                      {errors.address1 && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.address1}</div>}
                    </div>
                    <div className="mcl-form-group">
                      <label className="mcl-form-label">Address 2</label>
                      <input className="mcl-form-input" value={formData.address2} onChange={(e) => updateField('address2', e.target.value)} data-testid="input-address2" placeholder="Apt, Suite, Unit (optional)" />
                    </div>
                    <div className="mcl-form-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                      <div className="mcl-form-group">
                        <label className="mcl-form-label">City *</label>
                        <input className={`mcl-form-input ${errors.city ? 'is-invalid' : ''}`} value={formData.city} onChange={(e) => updateField('city', e.target.value)} data-testid="input-city" placeholder="City" />
                        {errors.city && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.city}</div>}
                      </div>
                      <div className="mcl-form-group">
                        <label className="mcl-form-label">State *</label>
                        <Select value={formData.state} onValueChange={(v) => updateField('state', v)}>
                          <SelectTrigger data-testid="select-state" className={errors.state ? 'border-red-400' : ''}><SelectValue placeholder="Select state" /></SelectTrigger>
                          <SelectContent>{US_STATES.map((s) => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}</SelectContent>
                        </Select>
                        {errors.state && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.state}</div>}
                      </div>
                      <div className="mcl-form-group">
                        <label className="mcl-form-label">Zip Code *</label>
                        <input className={`mcl-form-input ${errors.zip ? 'is-invalid' : ''}`} inputMode="numeric" value={formData.zip} onChange={(e) => updateField('zip', formatZipCode(e.target.value))} data-testid="input-zip" placeholder="12345" />
                        {errors.zip && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.zip}</div>}
                      </div>
                    </div>
                    <div className="mcl-form-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                      <div className="mcl-form-group">
                        <label className="mcl-form-label">Phone Number *</label>
                        <input className={`mcl-form-input ${errors.phoneNumber ? 'is-invalid' : ''}`} type="tel" value={formatPhoneNumber(formData.phoneNumber)} onChange={(e) => updateField('phoneNumber', parsePhoneNumber(e.target.value))} data-testid="input-phone" placeholder="(555) 123-4567" />
                        {errors.phoneNumber && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.phoneNumber}</div>}
                      </div>
                      <div className="mcl-form-group">
                        <label className="mcl-form-label">Date of Birth *</label>
                        <input className={`mcl-form-input ${errors.dateOfBirth ? 'is-invalid' : ''}`} type="date" value={formData.dateOfBirth} onChange={(e) => updateField('dateOfBirth', e.target.value)} data-testid="input-dob" />
                        {errors.dateOfBirth && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.dateOfBirth}</div>}
                      </div>
                      <div className="mcl-form-group">
                        <label className="mcl-form-label">SSN (Last 4 digits)</label>
                        <input className="mcl-form-input" value={formData.ssn} onChange={(e) => { const val = e.target.value.replace(/\D/g, '').slice(0, 4); updateField('ssn', val); }} data-testid="input-ssn" placeholder="1234" maxLength={4} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sections 2 & 3 side by side on large screens */}
                <div className="mcl-sections-row">
                {/* Section 2: Case Details */}
                <div className="mcl-card mb-3">
                  <div className="mcl-card-header"><h3 className="mcl-card-header-title">2. Case Details</h3></div>
                  <div className="mcl-card-body">
                    <div className="mcl-form-group">
                      <label className="mcl-form-label">Case Type *</label>
                      <Select value={formData.caseType} onValueChange={(v) => updateField('caseType', v)}>
                        <SelectTrigger data-testid="select-case-type" className={errors.caseType ? 'border-red-400' : ''}><SelectValue placeholder="Select case type" /></SelectTrigger>
                        <SelectContent>{CASE_TYPES.map((c) => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}</SelectContent>
                      </Select>
                      {errors.caseType && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.caseType}</div>}
                      {requiresPoliceReport(formData.caseType) && <PoliceReportNotice variant="notice" />}
                    </div>
                    {formData.caseType === 'other' && (
                      <div className="mcl-form-group">
                        <label className="mcl-form-label">Case Type Description *</label>
                        <input className={`mcl-form-input ${errors.caseTypeOtherText ? 'is-invalid' : ''}`} value={formData.caseTypeOtherText} onChange={(e) => updateField('caseTypeOtherText', e.target.value)} data-testid="input-case-type-other" placeholder="Please describe your case type" />
                        {errors.caseTypeOtherText && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.caseTypeOtherText}</div>}
                      </div>
                    )}
                    <div className="mcl-form-row">
                    <div className="mcl-form-group">
                      <label className="mcl-form-label">Date of Incident</label>
                      <input className="mcl-form-input" type="date" value={formData.incidentDate} onChange={(e) => updateField('incidentDate', e.target.value)} data-testid="input-incident-date" />
                    </div>
                    <div className="mcl-form-group">
                      <label className="mcl-form-label">How much money are you looking for? *</label>
                      <div style={{ position: 'relative' }}>
                        <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--mcl-text-muted)', fontWeight: 500 }}>$</span>
                        <input className={`mcl-form-input ${errors.requestedAmount ? 'is-invalid' : ''}`} type="text" value={formatCurrency(formData.requestedAmount)} onChange={(e) => updateField('requestedAmount', parseCurrency(e.target.value))} data-testid="input-requested-amount" placeholder="0.00" style={{ paddingLeft: '28px' }} />
                      </div>
                      {errors.requestedAmount && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.requestedAmount}</div>}
                    </div>
                    </div>
                    <div className="mcl-form-group">
                      <label className="mcl-form-label">In what state did the accident occur? *</label>
                      <div style={{ maxWidth: '280px' }}>
                        <Select value={formData.accidentState} onValueChange={(v) => updateField('accidentState', v)}>
                          <SelectTrigger data-testid="select-accident-state" className={errors.accidentState ? 'border-red-400' : ''}><SelectValue placeholder="Select state" /></SelectTrigger>
                          <SelectContent>{US_STATES.map((s) => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                      {errors.accidentState && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.accidentState}</div>}
                    </div>
                    <div className="mcl-form-row">
                    <div className="mcl-form-group">
                      <label className="mcl-form-label">Have you already received pre-settlement funding? *</label>
                      <RadioGroup name="hasPriorFunding" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} value={formData.hasPriorFunding} onChange={(v) => updateField('hasPriorFunding', v)} required />
                      {errors.hasPriorFunding && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.hasPriorFunding}</div>}
                    </div>
                    <div className="mcl-form-group">
                      <label className="mcl-form-label">Any offers or has your case settled? *</label>
                      <RadioGroup name="hasOfferOrSettled" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} value={formData.hasOfferOrSettled} onChange={(v) => updateField('hasOfferOrSettled', v)} required />
                      {errors.hasOfferOrSettled && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.hasOfferOrSettled}</div>}
                    </div>
                    </div>
                  </div>
                </div>

                {/* Section 3: Insurance Information */}
                <div className="mcl-card mb-3">
                  <div className="mcl-card-header"><h3 className="mcl-card-header-title">3. Insurance Information</h3></div>
                  <div className="mcl-card-body">
                    <p style={{ color: 'var(--mcl-text-muted)', fontSize: '0.875rem', marginBottom: '1.25rem' }}>Please provide any available insurance details related to your case. This helps funders better evaluate your application.</p>
                    <div className="mcl-form-group">
                      <label className="mcl-form-label">Insurance Carrier *</label>
                      <Select value={formData.insuranceCarrier} onValueChange={(v) => updateField('insuranceCarrier', v)}>
                        <SelectTrigger data-testid="select-insurance-carrier" className={errors.insuranceCarrier ? 'border-red-400' : ''}><SelectValue placeholder="Select insurance carrier" /></SelectTrigger>
                        <SelectContent>{INSURANCE_CARRIERS.map((c) => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}</SelectContent>
                      </Select>
                      {errors.insuranceCarrier && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.insuranceCarrier}</div>}
                    </div>
                    {formData.insuranceCarrier === 'other' && (
                      <div className="mcl-form-group">
                        <label className="mcl-form-label">Enter Insurance Carrier Name *</label>
                        <input className={`mcl-form-input ${errors.insuranceCarrierOther ? 'is-invalid' : ''}`} value={formData.insuranceCarrierOther} onChange={(e) => updateField('insuranceCarrierOther', e.target.value)} data-testid="input-insurance-carrier-other" placeholder="Enter carrier name" />
                        {errors.insuranceCarrierOther && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.insuranceCarrierOther}</div>}
                      </div>
                    )}
                    <div className="mcl-form-group">
                      <label className="mcl-form-label">Liability Policy Limits</label>
                      <Select value={formData.liabilityLimits} onValueChange={(v) => updateField('liabilityLimits', v)}>
                        <SelectTrigger data-testid="select-liability-limits"><SelectValue placeholder="Select policy limits" /></SelectTrigger>
                        <SelectContent>{LIABILITY_LIMITS.map((l) => (<SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>))}</SelectContent>
                      </Select>
                    </div>
                    <div className="mcl-form-group">
                      <label className="mcl-form-label">Do you have Uninsured Motorist (UM) Coverage? *</label>
                      <RadioGroup name="hasUmCoverage" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} value={formData.hasUmCoverage} onChange={(v) => updateField('hasUmCoverage', v)} required />
                      {errors.hasUmCoverage && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.hasUmCoverage}</div>}
                    </div>
                    {formData.hasUmCoverage === 'yes' && (
                      <>
                        <div className="mcl-form-group">
                          <label className="mcl-form-label">UM Policy Limits *</label>
                          <Select value={formData.umLimits} onValueChange={(v) => updateField('umLimits', v)}>
                            <SelectTrigger data-testid="select-um-limits" className={errors.umLimits ? 'border-red-400' : ''}><SelectValue placeholder="Select UM limits" /></SelectTrigger>
                            <SelectContent>{LIABILITY_LIMITS.map((l) => (<SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>))}</SelectContent>
                          </Select>
                          {errors.umLimits && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.umLimits}</div>}
                        </div>
                        <div className="mcl-form-row">
                          <div className="mcl-form-group">
                            <label className="mcl-form-label">UM Policy Number</label>
                            <input className="mcl-form-input" value={formData.umPolicyNumber} onChange={(e) => updateField('umPolicyNumber', e.target.value)} data-testid="input-um-policy-number" placeholder="e.g. POL-12345" />
                          </div>
                          <div className="mcl-form-group">
                            <label className="mcl-form-label">UM Claim Number</label>
                            <input className="mcl-form-input" value={formData.umClaimNumber} onChange={(e) => updateField('umClaimNumber', e.target.value)} data-testid="input-um-claim-number" placeholder="e.g. CLM-67890" />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                </div>{/* end mcl-sections-row */}

                {/* Section 4: Law Firm Information */}
                <div className="mcl-card mb-3">
                  <div className="mcl-card-header"><h3 className="mcl-card-header-title">4. Law Firm Information</h3></div>
                  <div className="mcl-card-body">
                    <div className="mcl-form-row">
                      <div className="mcl-form-group" style={{ position: 'relative' }}>
                        <label className="mcl-form-label">Lawyer Name</label>
                        <input className={`mcl-form-input ${errors.lawyerName ? 'is-invalid' : ''}`} value={formData.lawyerName} onChange={(e) => handleLawyerSearch(e.target.value, 'name')} onFocus={() => formData.lawyerName.length >= 2 && searchLawyers(formData.lawyerName, 'name')} onBlur={() => setTimeout(() => setShowLawyerDropdown(false), 200)} data-testid="input-lawyer-name" placeholder="Search by lawyer name..." autoComplete="off" />
                        {showLawyerDropdown && lawyerSearchField === 'name' && lawyerSearchResults.length > 0 && (
                          <div style={{ position: 'absolute', zIndex: 50, width: '100%', marginTop: '4px', background: '#fff', border: '1px solid var(--mcl-border)', borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: '240px', overflowY: 'auto' }} data-testid="lawyer-dropdown">
                            {lawyerSearchResults.map((lawyer, index) => (
                              <button key={lawyer.id || lawyer.email || `name-${index}`} type="button" style={{ width: '100%', padding: '10px 14px', textAlign: 'left', borderBottom: '1px solid var(--mcl-border-light)', background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => handleLawyerSelect(lawyer)} data-testid={`lawyer-option-${index}`} onMouseEnter={(e) => e.target.style.backgroundColor = '#f1f5f9'} onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}>
                                <div style={{ fontWeight: 600, color: 'var(--mcl-text-heading)', fontSize: '0.875rem' }}>{lawyer.fullName}</div>
                                <div style={{ fontSize: '0.8rem', color: 'var(--mcl-text-muted)' }}>{lawyer.email}</div>
                                {lawyer.lawFirmName && <div style={{ fontSize: '0.8rem', color: 'var(--mcl-primary)' }}>{lawyer.lawFirmName}</div>}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="mcl-form-group" style={{ position: 'relative' }}>
                        <label className="mcl-form-label">Attorney Email *</label>
                        <input className={`mcl-form-input ${errors.attorneyEmail ? 'is-invalid' : ''}`} type="email" value={formData.attorneyEmail} onChange={(e) => handleLawyerSearch(e.target.value, 'email')} onFocus={() => formData.attorneyEmail.length >= 2 && searchLawyers(formData.attorneyEmail, 'email')} onBlur={() => setTimeout(() => setShowLawyerDropdown(false), 200)} data-testid="input-attorney-email" placeholder="Search or enter email..." autoComplete="off" />
                        {errors.attorneyEmail && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.attorneyEmail}</div>}
                        {showLawyerDropdown && lawyerSearchField === 'email' && lawyerSearchResults.length > 0 && (
                          <div style={{ position: 'absolute', zIndex: 50, width: '100%', marginTop: '4px', background: '#fff', border: '1px solid var(--mcl-border)', borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: '240px', overflowY: 'auto' }} data-testid="lawyer-email-dropdown">
                            {lawyerSearchResults.map((lawyer, index) => (
                              <button key={lawyer.id || lawyer.email || `email-${index}`} type="button" style={{ width: '100%', padding: '10px 14px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => handleLawyerSelect(lawyer)} data-testid={`lawyer-email-option-${index}`} onMouseEnter={(e) => e.target.style.backgroundColor = '#f1f5f9'} onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}>
                                <div style={{ fontWeight: 600, color: 'var(--mcl-text-heading)', fontSize: '0.875rem' }}>{lawyer.email}</div>
                                <div style={{ fontSize: '0.8rem', color: 'var(--mcl-text-muted)' }}>{lawyer.fullName}</div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    {lawyerAutoFilled && (
                      <div className="mcl-alert mcl-alert-info" style={{ marginBottom: '1rem' }}>
                        Law firm details filled from our lawyer directory
                      </div>
                    )}
                    <div className="mcl-form-row">
                      <div className="mcl-form-group">
                        <label className="mcl-form-label">Law Firm *</label>
                        <LawFirmTypeahead
                          value={formData.lawFirmName}
                          firmId={formData.firm_id}
                          onChange={({ firm_id, name }) => setFormData(prev => ({ ...prev, lawFirmName: name, firm_id }))}
                          hasError={!!errors.lawFirmName}
                          placeholder="Search or type your law firm…"
                          inputTestId="input-law-firm-name"
                        />
                        {errors.lawFirmName && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.lawFirmName}</div>}
                      </div>
                      <div className="mcl-form-group">
                        <label className="mcl-form-label">Law Firm Phone *</label>
                        <input className={`mcl-form-input ${errors.lawFirmPhone ? 'is-invalid' : ''}`} type="tel" value={formatPhoneNumber(formData.lawFirmPhone)} onChange={(e) => updateField('lawFirmPhone', parsePhoneNumber(e.target.value))} data-testid="input-law-firm-phone" placeholder="(555) 123-4567" />
                        {errors.lawFirmPhone && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.lawFirmPhone}</div>}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 5: Injuries & Treatments */}
                <div className="mcl-card mb-3">
                  <div className="mcl-card-header"><h3 className="mcl-card-header-title">5. Injuries & Treatments</h3></div>
                  <div className="mcl-card-body">
                    <div className="mcl-form-group">
                      <label className="mcl-form-label">What are your injuries? *</label>
                      <textarea className={`mcl-form-textarea ${errors.injuriesDescription ? 'is-invalid' : ''}`} value={formData.injuriesDescription} onChange={(e) => updateField('injuriesDescription', e.target.value)} data-testid="input-injuries-description" rows={3} placeholder="Describe your injuries..." />
                      {errors.injuriesDescription && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.injuriesDescription}</div>}
                    </div>
                    <div className="mcl-form-group">
                      <label className="mcl-form-label">Have you started treatment? *</label>
                      <RadioGroup name="hasStartedTreatment" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} value={formData.hasStartedTreatment} onChange={(v) => updateField('hasStartedTreatment', v)} required />
                      {errors.hasStartedTreatment && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.hasStartedTreatment}</div>}
                    </div>
                    {formData.hasStartedTreatment === 'yes' && (
                      <>
                        <div className="mcl-form-group">
                          <label className="mcl-form-label">Where did you go? *</label>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            {[{ value: 'hospital', label: 'Hospital' }, { value: 'urgent_care', label: 'Urgent Care' }, { value: 'emergency_room', label: 'Emergency Room' }, { value: 'pcp', label: 'PCP - Primary Care Physician' }, { value: 'specialist', label: 'Specialist' }].map((option) => (
                              <label key={option.value} className="mcl-checkbox-label">
                                <input type="checkbox" checked={formData.treatmentLocations?.includes(option.value) || false} onChange={(e) => { const current = formData.treatmentLocations || []; updateField('treatmentLocations', e.target.checked ? [...current, option.value] : current.filter(v => v !== option.value)); }} data-testid={`checkbox-location-${option.value}`} />
                                <span>{option.label}</span>
                              </label>
                            ))}
                          </div>
                          {errors.treatmentLocations && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.treatmentLocations}</div>}
                        </div>
                        <div className="mcl-form-group">
                          <label className="mcl-form-label">How many treatments have you completed? *</label>
                          <RadioGroup name="treatmentsCompleted" options={[{ value: '1-5', label: '1-5' }, { value: '6-10', label: '6-10' }, { value: '11-20', label: '11-20' }, { value: '20+', label: '20+' }]} value={formData.treatmentsCompleted} onChange={(v) => updateField('treatmentsCompleted', v)} required />
                          {errors.treatmentsCompleted && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.treatmentsCompleted}</div>}
                        </div>
                        <div className="mcl-form-group">
                          <label className="mcl-form-label">Any surgical recommendations? *</label>
                          <RadioGroup name="surgicalRecommendations" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} value={formData.surgicalRecommendations} onChange={(v) => updateField('surgicalRecommendations', v)} required />
                          {errors.surgicalRecommendations && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.surgicalRecommendations}</div>}
                        </div>
                      </>
                    )}
                    <div className="mcl-form-group">
                      <label className="mcl-form-label">Have you had physical therapy or seen a chiropractor? *</label>
                      <RadioGroup name="hadPtOrChiro" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'not_sure', label: 'Not Sure' }]} value={formData.hadPtOrChiro} onChange={(v) => updateField('hadPtOrChiro', v)} required />
                      {errors.hadPtOrChiro && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.hadPtOrChiro}</div>}
                    </div>
                    <div className="mcl-form-group">
                      <label className="mcl-form-label">Have you had any serious injections? *</label>
                      <RadioGroup name="hadSeriousInjections" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'not_sure', label: 'Not Sure' }, { value: 'expecting', label: 'Expecting' }]} value={formData.hadSeriousInjections} onChange={(v) => updateField('hadSeriousInjections', v)} required />
                      <div className="mcl-form-hint">For example: Epidural or facet injection (not cortisone)</div>
                      {errors.hadSeriousInjections && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.hadSeriousInjections}</div>}
                    </div>
                    <div className="mcl-form-group">
                      <label className="mcl-form-label">Did you break any bones? *</label>
                      <RadioGroup name="brokeBones" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'unknown', label: 'Unknown' }]} value={formData.brokeBones} onChange={(v) => updateField('brokeBones', v)} required />
                      {errors.brokeBones && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.brokeBones}</div>}
                    </div>
                    <div className="mcl-form-group">
                      <label className="mcl-form-label">Did you ever have surgery? *</label>
                      <RadioGroup name="hadSurgery" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'expecting', label: 'Expecting' }]} value={formData.hadSurgery} onChange={(v) => updateField('hadSurgery', v)} required />
                      {errors.hadSurgery && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.hadSurgery}</div>}
                    </div>
                    <div className="mcl-form-group">
                      <label className="mcl-form-label">Any other injuries/treatments?</label>
                      <textarea className="mcl-form-textarea" value={formData.otherInjuriesTreatments} onChange={(e) => updateField('otherInjuriesTreatments', e.target.value)} data-testid="input-other-injuries" rows={3} placeholder="Broken teeth, brain damage, stitches, etc." />
                    </div>
                    <div className="mcl-form-group">
                      <label className="mcl-form-label">Comments</label>
                      <textarea className="mcl-form-textarea" value={formData.comments} onChange={(e) => updateField('comments', e.target.value)} data-testid="input-comments" rows={3} placeholder="Any additional information (optional)" />
                    </div>
                  </div>
                </div>

                {/* Section 6: Additional Information + Section 7: Supporting Documents (responsive 2-col grid) */}
                <div className="mcl-form-row mb-3" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1rem' }}>
                  <style>{`@media (min-width: 992px) { .mcl-section-grid-6-7 { grid-template-columns: 1fr 1fr !important; } }`}</style>
                  <div className="mcl-section-grid-6-7" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1rem', gridColumn: '1 / -1' }}>
                    <div className="mcl-card">
                      <div className="mcl-card-header"><h3 className="mcl-card-header-title">6. Additional Information</h3></div>
                      <div className="mcl-card-body">
                        <div className="mcl-form-group">
                          <label className="mcl-form-label">Do you have any child support liens? *</label>
                          <RadioGroup name="childSupportLiens" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'unknown', label: 'Unknown' }]} value={formData.childSupportLiens} onChange={(v) => updateField('childSupportLiens', v)} required />
                          {errors.childSupportLiens && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.childSupportLiens}</div>}
                        </div>
                        <div className="mcl-form-group">
                          <label className="mcl-form-label">Are you currently in bankruptcy? *</label>
                          <RadioGroup name="inBankruptcy" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} value={formData.inBankruptcy} onChange={(v) => updateField('inBankruptcy', v)} required />
                          {errors.inBankruptcy && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.inBankruptcy}</div>}
                        </div>
                      </div>
                    </div>

                    <div className="mcl-card" data-testid="form-document-upload-column">
                      <div className="mcl-card-header"><h3 className="mcl-card-header-title">7. Supporting Documents</h3></div>
                      <div className="mcl-card-body">
                        {currentDraftAppId ? (
                          <DocumentUpload applicationId={currentDraftAppId} canUpload={true} getAuthHeaders={getAuthHeaders} userRole="client" />
                        ) : (
                          <div style={{ padding: '1rem', background: '#f8fafc', border: '1px dashed var(--mcl-border)', borderRadius: '8px', textAlign: 'center', color: 'var(--mcl-text-muted)' }} data-testid="upload-locked-message">
                            <FileText size={28} style={{ color: 'var(--mcl-text-muted)', marginBottom: '0.5rem' }} />
                            <p style={{ fontSize: '0.9rem', marginBottom: '0.75rem', fontWeight: 500 }}>Save your application as a draft to upload supporting documents.</p>
                            <p style={{ fontSize: '0.8rem', marginBottom: '0.75rem' }}>Police reports, medical records, photos, and other case documents can be added here.</p>
                            <button type="submit" className="mcl-btn mcl-btn-sm mcl-btn-primary" disabled={savingDraft} data-testid="save-draft-inline-button">
                              <Save size={14} /> {savingDraft ? 'Saving...' : 'Save Draft to Unlock Uploads'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Submit Buttons */}
                {/* Pre-submit reminder: nudge MVA / Rideshare filers to attach a police report as soon as possible. */}
                {requiresPoliceReport(formData.caseType) && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <PoliceReportNotice variant="reminder" />
                  </div>
                )}
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <button type="submit" formNoValidate className="mcl-btn mcl-btn-outline" disabled={savingDraft} data-testid="save-draft-button"><Save size={16} /> {savingDraft ? 'Saving...' : (currentDraftAppId ? 'Update Draft' : 'Save Draft')}</button>
                  <button type="button" className="mcl-btn mcl-btn-primary" data-testid="submit-to-lawyer-button" onClick={handleSubmitToLawyer}>Submit to Lawyer</button>
                  <button type="button" className="mcl-btn mcl-btn-ghost" onClick={() => { setShowForm(false); setFormData(initialFormData); setErrors({}); setCurrentDraftAppId(null); navigate('/client'); }} data-testid="cancel-application-form">Cancel</button>
                </div>
              </form>
            </SectionCard>
          )}

          {/* Pending Offers Alert */}
          {applications.some(a => a.pending_offers_count > 0) && (
            <div data-testid="pending-offers-alert" style={{ background: 'linear-gradient(135deg, #eff6ff, #dbeafe)', border: '1px solid #93c5fd', borderRadius: '10px', padding: '1rem 1.25rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <DollarSign size={18} color="#fff" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, color: '#1e40af', fontSize: '0.9375rem' }}>You have funding offers waiting for review!</div>
                <div style={{ fontSize: '0.8125rem', color: '#3b82f6' }}>
                  {applications.filter(a => a.pending_offers_count > 0).map(a => `${a.firstName} ${a.lastName}`).join(', ')} — click &quot;Review Offers&quot; to compare and accept.
                </div>
              </div>
            </div>
          )}

          {/* Applications List */}
          <div className="mcl-card" data-testid="applications-list">
            <div className="mcl-card-header">
              <h3 className="mcl-card-header-title">Applications ({applications.length})</h3>
            </div>
            {applications.length === 0 ? (
              <EmptyState icon={FileText} title="No applications yet" text="Create your first funding application to get started." actions={<button className="mcl-btn mcl-btn-primary mcl-btn-sm" onClick={() => setShowForm(true)}><Plus size={14} /> New Application</button>} />
            ) : (
              <div className="mcl-table-wrapper">
                <table className="mcl-table">
                  <thead>
                    <tr>
                      <th>Applicant</th>
                      <th>Case Type</th>
                      <th>Amount</th>
                      <th>Law Firm</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {applications.map((app) => (
                      <tr key={app.application_id} data-testid={`application-${app.application_id}`}>
                        <td>
                          <div style={{ fontWeight: 600, color: 'var(--mcl-text-heading)' }}>{app.firstName} {app.lastName}</div>
                          {app.city && app.state && <div style={{ fontSize: '0.8rem', color: 'var(--mcl-text-muted)' }}>{app.city}, {app.state}</div>}
                        </td>
                        <td>{getCaseTypeLabel(app.caseType || app.case_type)}</td>
                        <td style={{ fontWeight: 600 }}>{app.requestedAmount ? `$${parseFloat(app.requestedAmount).toLocaleString()}` : '-'}</td>
                        <td>{app.lawFirmName || app.lawyer_name || '-'}</td>
                        <td><StatusBadge status={app.client_display_status || app.status} /></td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.4rem' }}>
                            {app.status === 'draft' && (
                              <>
                                <button className="mcl-btn mcl-btn-sm mcl-btn-primary" onClick={() => handleSubmitApplication(app.application_id)} data-testid={`submit-to-lawyer-button-${app.application_id}`}>Submit</button>
                                <button className="mcl-action-btn" onClick={() => { setSelectedApplication(app); setShowCancelDialog(true); }} data-testid={`delete-draft-button-${app.application_id}`} title="Delete draft" style={{ color: 'var(--mcl-danger)', borderColor: 'var(--mcl-danger)' }}>
                                  <Trash2 size={14} /> Delete
                                </button>
                              </>
                            )}
                            {app.pending_offers_count > 0 && (
                              <button className="mcl-btn mcl-btn-sm mcl-btn-success" onClick={() => onViewApplication ? onViewApplication(app.application_id) : setViewingApplication(app)} data-testid={`review-offers-${app.application_id}`}>
                                <Eye size={14} /> Review Offer{app.pending_offers_count > 1 ? 's' : ''}
                              </button>
                            )}
                            {!app.pending_offers_count && (
                              <button className="mcl-action-btn primary" onClick={() => onViewApplication ? onViewApplication(app.application_id) : setViewingApplication(app)} data-testid={`view-button-${app.application_id}`}>
                                <Eye size={14} /> View
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Cancel / Delete dialog */}
      {(() => {
        const isDraft = selectedApplication?.status === 'draft';
        return (
          <ConfirmDialog
            open={showCancelDialog}
            onOpenChange={setShowCancelDialog}
            tone="danger"
            title={isDraft ? 'Delete Draft?' : 'Cancel Application?'}
            description={isDraft
              ? 'This draft and any documents uploaded to it will be permanently deleted.'
              : 'Your application will be withdrawn and funders will no longer be able to review it.'}
            warning={selectedApplication?.status === 'approved'
              ? 'This cannot be undone. This application has already been approved — canceling removes it from funders’ view.'
              : 'This cannot be undone.'}
            confirmLabel={isDraft ? 'Yes, Delete Draft' : 'Yes, Cancel Application'}
            busyLabel={isDraft ? 'Deleting...' : 'Canceling...'}
            busy={cancellingApp}
            onConfirm={handleCancelApplication}
            cancelLabel={isDraft ? 'Keep Draft' : 'Keep Application'}
            testId="confirm-cancel-application"
          />
        );
      })()}
    </div>
  );
};

/* ================================================================
   Application Detail View
   ================================================================ */
// Named export so the save path can be exercised directly in tests — the
// blanks-block-the-save regression was invisible to unit tests of the
// validators alone, because the bug was in the gate, not the rules.
export const ApplicationDetailView = ({ application, onBack, onUpdate, userRole = 'client', onCancel, canCancel, currentUser }) => {
  const location = useLocation();
  const justCompletedApply = !!location.state?.justCompletedApply;
  const [showOnboardingBanner, setShowOnboardingBanner] = useState(justCompletedApply);
  // Auto-open the editor when the client just finished the public Apply-Now
  // flow — landing them in "fill in the rest" mode rather than a read-only view.
  const [isEditingAll, setIsEditingAll] = useState(justCompletedApply);
  const [formData, setFormData] = useState({ ...application });
  const [originalData, setOriginalData] = useState({ ...application });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  // Activity Timeline sources — offers + document requests. Both endpoints
  // return [] gracefully for unauthorised users so this is safe cross-role.
  const [offers, setOffers] = useState([]);
  const [documentRequests, setDocumentRequests] = useState([]);
  // Docs list so we can suppress the Police-Report notice once a police report
  // has already been uploaded on this application.
  const [documents, setDocuments] = useState([]);

  // Clear the navigation state on first mount so a browser refresh doesn't
  // re-trigger the onboarding UX.
  useEffect(() => {
    if (justCompletedApply) {
      window.history.replaceState({}, document.title);
    }
  }, [justCompletedApply]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  // Refresh timeline sources whenever the application id changes (and on demand
  // via onChanged callbacks from child cards).
  const fetchTimelineSources = async () => {
    const token = localStorage.getItem('access_token');
    const hdr = token ? { 'Authorization': `Bearer ${token}` } : {};
    try {
      const [oRes, dRes, docsRes] = await Promise.all([
        fetch(`${API}/applications/${application.application_id}/offers`, { credentials: 'include', headers: hdr }),
        fetch(`${API}/applications/${application.application_id}/document-requests`, { credentials: 'include', headers: hdr }),
        fetch(`${API}/applications/${application.application_id}/documents`, { credentials: 'include', headers: hdr }),
      ]);
      if (oRes.ok) {
        const od = await oRes.json();
        setOffers(Array.isArray(od) ? od : (od.offers || []));
      }
      if (dRes.ok) {
        const dd = await dRes.json();
        setDocumentRequests(dd.requests || []);
      }
      if (docsRes.ok) {
        const docsData = await docsRes.json();
        setDocuments(docsData.documents || []);
      }
    } catch (e) { /* timeline degrades gracefully */ }
  };

  useEffect(() => { fetchTimelineSources(); }, [application.application_id]);

  const canEdit = () => {
    if (userRole === 'funder') return false;
    // The admin override, and it has to sit ABOVE the status gate. Bad data on
    // an approved or funded case has no other remedy — the client and the
    // attorney are both locked out, here and in update_application — so an
    // admin who cannot edit exactly those applications cannot do the job the
    // override exists for. Moving this line below the gate still reads fine
    // and still passes a casual click-through on a draft. Ordering is
    // load-bearing; AdminEditApplication.test.js pins it per status.
    if (userRole === 'admin') return true;
    if (['approved', 'funded'].includes(application.status)) return false;
    if (userRole === 'lawyer') return true;
    if (userRole === 'client') return true;
    return false;
  };

  const handleEditAllClick = () => {
    setIsEditingAll(true);
    setOriginalData({ ...formData });
  };

  const handleCancelClick = () => { setFormData({ ...originalData }); setIsEditingAll(false); setErrors({}); };

  const handleSaveAll = async () => {
    // Blanks must never block: an application arrives from `/apply` with
    // sixteen of them by design, and blocking here stranded every edit the
    // applicant made. Only malformed input stops the save.
    const formatErrors = validateApplicationFormats(formData);
    setErrors(formatErrors);
    if (Object.keys(formatErrors).length > 0) {
      toast.error('Please fix the highlighted fields before saving');
      return;
    }
    setSaving(true);
    try {
      await apiPut(`/applications/${application.application_id}`, formData);
      const stillMissing = getMissingRequiredFields(formData).length;
      toast.success(stillMissing
        ? `Saved — ${stillMissing} field${stillMissing === 1 ? '' : 's'} left to complete`
        : 'Application saved');
      setOriginalData({ ...formData }); setIsEditingAll(false); setErrors({}); if (onUpdate) onUpdate();
    } catch (error) { toast.error(error.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const updateField = (field, value) => { setFormData({ ...formData, [field]: value }); if (errors[field]) setErrors({ ...errors, [field]: null }); };
  const editing = isEditingAll;
  const getRadioDisplayValue = (value, options) => { const option = options.find(o => o.value === value); return option ? option.label : value || 'Not provided'; };

  const renderDetailField = (label, editContent, displayValue, required) => (
    <div className="mcl-form-group">
      <label className="mcl-form-label">{label}{required && ' *'}</label>
      {editContent || <ReadOnlyValue value={displayValue} />}
    </div>
  );

  return (
    <div data-testid="application-detail-view">
      <BackLink onClick={onBack} label="Back to Applications" />

      {showOnboardingBanner && (() => {
        const { pct, filled, total, missingCount } = computeClientCompletion(formData);
        const isComplete = missingCount === 0;
        return (
        <div
          data-testid="onboarding-banner"
          style={{
            marginBottom: '1.25rem',
            padding: '1rem 1.1rem',
            background: isComplete
              ? 'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)'
              : 'linear-gradient(135deg, #ecfeff 0%, #eff6ff 100%)',
            border: `1px solid ${isComplete ? '#86efac' : '#93c5fd'}`,
            borderLeft: `4px solid ${isComplete ? '#16a34a' : '#2563eb'}`,
            borderRadius: '10px',
            display: 'flex',
            gap: '0.85rem',
            alignItems: 'flex-start',
          }}
        >
          <span style={{
            display: 'inline-flex', flexShrink: 0,
            width: '28px', height: '28px', borderRadius: '50%',
            background: isComplete ? '#16a34a' : '#2563eb', color: '#fff',
            alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: '0.85rem',
          }} aria-hidden>{isComplete ? '✓' : '!'}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, color: isComplete ? '#14532d' : '#1e3a8a', fontSize: '0.95rem', marginBottom: '0.25rem' }}>
              {isComplete
                ? 'Application 100% complete — nice work!'
                : 'Your application has been submitted — please finish filling it out'}
            </div>
            <div style={{ fontSize: '0.85rem', color: isComplete ? '#166534' : '#1e40af', lineHeight: 1.45, marginBottom: '0.55rem' }}>
              {isComplete
                ? 'Every required field is filled in. Don\u2019t forget to upload supporting documents at the bottom of the page — they help funders make stronger offers.'
                : <>The Apply Now form only captured the essentials. The more you complete below (injuries,
                    treatment, insurance, attorney details), the stronger and faster the offers from funders.
                    You can also upload supporting documents at the bottom of the page.</>}
            </div>
            {/* Live progress bar */}
            <div data-testid="onboarding-progress" style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginTop: '0.15rem' }}>
              <div style={{ flex: 1, maxWidth: '320px', height: '8px', background: '#dbeafe', borderRadius: '999px', overflow: 'hidden' }}>
                <div
                  data-testid="onboarding-progress-bar"
                  style={{
                    width: `${pct}%`,
                    height: '100%',
                    background: isComplete ? '#16a34a' : 'linear-gradient(90deg, #2563eb, #06b6d4)',
                    transition: 'width 220ms ease-out',
                  }}
                />
              </div>
              <span data-testid="onboarding-progress-text" style={{ fontSize: '0.78rem', color: isComplete ? '#166534' : '#1e3a8a', fontWeight: 600, whiteSpace: 'nowrap' }}>
                {pct}% complete · {isComplete ? 'all fields filled' : `${missingCount} of ${total} field${missingCount === 1 ? '' : 's'} left`}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowOnboardingBanner(false)}
            data-testid="dismiss-onboarding-banner"
            aria-label="Dismiss"
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: isComplete ? '#166534' : '#1e40af', padding: '0.25rem 0.4rem', fontSize: '0.8rem',
              fontWeight: 600,
            }}
          >Dismiss</button>
        </div>
        );
      })()}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
        <div>
          <h2 className="mcl-page-title">{formData.firstName} {formData.lastName}</h2>
          <p className="mcl-page-subtitle">Application ID: {application.application_id}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <StatusBadge status={application.client_display_status || application.status} />
          {canEdit() && !isEditingAll && (
            <button className="mcl-btn mcl-btn-sm mcl-btn-outline-primary" onClick={handleEditAllClick} data-testid="edit-application-button">
              <Pencil size={14} /> Edit Application
            </button>
          )}
          {isEditingAll && (
            <>
              <button className="mcl-btn mcl-btn-sm mcl-btn-success" onClick={handleSaveAll} disabled={saving} data-testid="save-all-button">
                <Save size={14} /> {saving ? 'Saving...' : 'Save All Changes'}
              </button>
              <button className="mcl-btn mcl-btn-sm mcl-btn-outline" onClick={handleCancelClick} data-testid="cancel-all-button">
                Cancel
              </button>
            </>
          )}
          {canCancel && onCancel && !isEditingAll && (
            <button className="mcl-btn mcl-btn-sm mcl-btn-danger" onClick={onCancel} data-testid="cancel-application-button">
              <XCircle size={14} /> Cancel Application
            </button>
          )}
        </div>
      </div>

      {/* Compare Offers Section - shown for approved/funded applications */}
      {['approved', 'funded'].includes(application.status) && (
        <CompareOffers applicationId={application.application_id} onOfferAccepted={onUpdate} canAccept={true} />
      )}

      {/* Section 1: My Information */}
      <SectionCard title="1. My Information" className="mb-3">
        <div className="mcl-form-row">
          {renderDetailField("First Name", editing && <><input className={`mcl-form-input ${errors.firstName ? 'is-invalid' : ''}`} value={formData.firstName || ''} onChange={(e) => updateField('firstName', e.target.value)} data-testid="edit-first-name" />{errors.firstName && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.firstName}</div>}</>, formData.firstName, true)}
          {renderDetailField("Last Name", editing && <><input className={`mcl-form-input ${errors.lastName ? 'is-invalid' : ''}`} value={formData.lastName || ''} onChange={(e) => updateField('lastName', e.target.value)} data-testid="edit-last-name" />{errors.lastName && <div className="mcl-form-hint" style={{ color: 'var(--mcl-danger)' }}>{errors.lastName}</div>}</>, formData.lastName, true)}
        </div>
        {renderDetailField("Address 1", editing && <input className={`mcl-form-input ${errors.address1 ? 'is-invalid' : ''}`} value={formData.address1 || ''} onChange={(e) => updateField('address1', e.target.value)} data-testid="edit-address1" />, formData.address1, true)}
        {renderDetailField("Address 2", editing && <input className="mcl-form-input" value={formData.address2 || ''} onChange={(e) => updateField('address2', e.target.value)} data-testid="edit-address2" />, formData.address2, false)}
        <div className="mcl-form-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          {renderDetailField("City", editing && <input className={`mcl-form-input ${errors.city ? 'is-invalid' : ''}`} value={formData.city || ''} onChange={(e) => updateField('city', e.target.value)} data-testid="edit-city" />, formData.city, true)}
          {renderDetailField("State", editing && <Select value={formData.state || ''} onValueChange={(v) => updateField('state', v)}><SelectTrigger data-testid="edit-state"><SelectValue placeholder="Select state" /></SelectTrigger><SelectContent>{US_STATES.map((s) => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}</SelectContent></Select>, US_STATES.find(s => s.value === formData.state)?.label || formData.state, true)}
          {renderDetailField("Zip Code", editing && <input className={`mcl-form-input ${errors.zip ? 'is-invalid' : ''}`} inputMode="numeric" value={formData.zip || ''} onChange={(e) => updateField('zip', formatZipCode(e.target.value))} data-testid="edit-zip" />, formData.zip, true)}
        </div>
        <div className="mcl-form-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          {renderDetailField("Phone Number", editing && <input className={`mcl-form-input ${errors.phoneNumber ? 'is-invalid' : ''}`} type="tel" value={formatPhoneNumber(formData.phoneNumber || '')} onChange={(e) => updateField('phoneNumber', parsePhoneNumber(e.target.value))} data-testid="edit-phone" placeholder="(555) 123-4567" />, formatPhoneNumber(formData.phoneNumber), true)}
          {renderDetailField("Date of Birth", editing && <input className={`mcl-form-input ${errors.dateOfBirth ? 'is-invalid' : ''}`} type="date" value={formData.dateOfBirth || ''} onChange={(e) => updateField('dateOfBirth', e.target.value)} data-testid="edit-dob" />, formatDateOnly(formData.dateOfBirth), true)}
          {renderDetailField("SSN (Last 4)", editing && <input className="mcl-form-input" value={formData.ssn || ''} onChange={(e) => updateField('ssn', e.target.value.replace(/\D/g, '').slice(0, 4))} data-testid="edit-ssn" maxLength={4} />, formData.ssn ? `****${formData.ssn}` : null, false)}
        </div>
      </SectionCard>

      {/* Sections 2 & 3 side by side */}
      <div className="mcl-sections-row">
      {/* Section 2: Case Details */}
      <SectionCard title="2. Case Details" className="mb-3">
        {renderDetailField("Case Type", editing && <Select value={formData.caseType || ''} onValueChange={(v) => updateField('caseType', v)}><SelectTrigger><SelectValue placeholder="Select case type" /></SelectTrigger><SelectContent>{CASE_TYPES.map((c) => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}</SelectContent></Select>, getCaseTypeLabel(formData.caseType), true)}
        {requiresPoliceReport(formData.caseType) && (
          <PoliceReportNotice
            variant="notice"
            hasPoliceReport={documents.some(d => d.document_category === 'police_accident_report')}
          />
        )}
        {(formData.caseType === 'other') && renderDetailField("Case Type Description", editing && <input className="mcl-form-input" value={formData.caseTypeOtherText || ''} onChange={(e) => updateField('caseTypeOtherText', e.target.value)} />, formData.caseTypeOtherText, true)}
        <div className="mcl-form-row">
        {renderDetailField("Date of Incident", editing && <input className="mcl-form-input" type="date" value={formData.incidentDate || ''} onChange={(e) => updateField('incidentDate', e.target.value)} />, formatDateOnly(formData.incidentDate), false)}
        {renderDetailField("Requested Amount", editing && <input className="mcl-form-input" value={formatCurrency(formData.requestedAmount)} onChange={(e) => updateField('requestedAmount', parseCurrency(e.target.value))} />, formData.requestedAmount ? `$${parseFloat(formData.requestedAmount).toLocaleString()}` : null, true)}
        </div>
        {renderDetailField("Accident State", editing && <Select value={formData.accidentState || ''} onValueChange={(v) => updateField('accidentState', v)}><SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger><SelectContent>{US_STATES.map((s) => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}</SelectContent></Select>, US_STATES.find(s => s.value === formData.accidentState)?.label || formData.accidentState, true)}
        <div className="mcl-form-row">
        {renderDetailField("Prior Funding", editing && <RadioGroup name="hasPriorFunding" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} value={formData.hasPriorFunding} onChange={(v) => updateField('hasPriorFunding', v)} />, getRadioDisplayValue(formData.hasPriorFunding, [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]), true)}
        {renderDetailField("Offers or Settled", editing && <RadioGroup name="hasOfferOrSettled" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} value={formData.hasOfferOrSettled} onChange={(v) => updateField('hasOfferOrSettled', v)} />, getRadioDisplayValue(formData.hasOfferOrSettled, [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]), true)}
        </div>
      </SectionCard>

      {/* Section 3: Insurance Information */}
      <SectionCard title="3. Insurance Information" className="mb-3">
        {renderDetailField("Insurance Carrier", editing && <Select value={formData.insuranceCarrier || ''} onValueChange={(v) => updateField('insuranceCarrier', v)}><SelectTrigger data-testid="edit-insurance-carrier" className={errors.insuranceCarrier ? 'border-red-400' : ''}><SelectValue placeholder="Select carrier" /></SelectTrigger><SelectContent>{INSURANCE_CARRIERS.map((c) => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}</SelectContent></Select>, getInsuranceCarrierLabel(formData.insuranceCarrier), true)}
        {(formData.insuranceCarrier === 'other') && renderDetailField("Carrier Name", editing && <input className="mcl-form-input" value={formData.insuranceCarrierOther || ''} onChange={(e) => updateField('insuranceCarrierOther', e.target.value)} />, formData.insuranceCarrierOther, true)}
        {renderDetailField("Liability Policy Limits", editing && <Select value={formData.liabilityLimits || ''} onValueChange={(v) => updateField('liabilityLimits', v)}><SelectTrigger><SelectValue placeholder="Select limits" /></SelectTrigger><SelectContent>{LIABILITY_LIMITS.map((l) => (<SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>))}</SelectContent></Select>, getLiabilityLimitsLabel(formData.liabilityLimits), false)}
        {renderDetailField("Uninsured Motorist (UM) Coverage", editing && <RadioGroup name="hasUmCoverage" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} value={formData.hasUmCoverage} onChange={(v) => updateField('hasUmCoverage', v)} />, getRadioDisplayValue(formData.hasUmCoverage, [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]), true)}
        {formData.hasUmCoverage === 'yes' && (
          <>
            {renderDetailField("UM Policy Limits", editing && <Select value={formData.umLimits || ''} onValueChange={(v) => updateField('umLimits', v)}><SelectTrigger className={errors.umLimits ? 'border-red-400' : ''}><SelectValue placeholder="Select UM limits" /></SelectTrigger><SelectContent>{LIABILITY_LIMITS.map((l) => (<SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>))}</SelectContent></Select>, getLiabilityLimitsLabel(formData.umLimits), true)}
            <div className="mcl-form-row">
              {renderDetailField("UM Policy Number", editing && <input className="mcl-form-input" value={formData.umPolicyNumber || ''} onChange={(e) => updateField('umPolicyNumber', e.target.value)} />, formData.umPolicyNumber, false)}
              {renderDetailField("UM Claim Number", editing && <input className="mcl-form-input" value={formData.umClaimNumber || ''} onChange={(e) => updateField('umClaimNumber', e.target.value)} />, formData.umClaimNumber, false)}
            </div>
          </>
        )}
      </SectionCard>
      </div>{/* end mcl-sections-row */}

      {/* Section 4: Law Firm */}
      <SectionCard title="4. Law Firm Information" className="mb-3">
        <div className="mcl-form-row">
          {renderDetailField("Lawyer Name", editing && <input className="mcl-form-input" value={formData.lawyerName || ''} onChange={(e) => updateField('lawyerName', e.target.value)} />, formData.lawyerName, false)}
          {renderDetailField("Attorney Email", editing && <input className={`mcl-form-input ${errors.attorneyEmail ? 'is-invalid' : ''}`} value={formData.attorneyEmail || ''} onChange={(e) => updateField('attorneyEmail', e.target.value)} autoComplete="off" />, formData.attorneyEmail, true)}
        </div>
        <div className="mcl-form-row">
          {renderDetailField("Law Firm", editing && <LawFirmTypeahead value={formData.lawFirmName || ''} firmId={formData.firm_id || ''} onChange={({ firm_id, name }) => setFormData(prev => ({ ...prev, lawFirmName: name, firm_id }))} hasError={!!errors.lawFirmName} placeholder="Search or type your law firm…" inputTestId="input-law-firm-name-edit" />, formData.lawFirmName, true)}
          {renderDetailField("Law Firm Phone", editing && <input className={`mcl-form-input ${errors.lawFirmPhone ? 'is-invalid' : ''}`} type="tel" value={formatPhoneNumber(formData.lawFirmPhone || '')} onChange={(e) => updateField('lawFirmPhone', parsePhoneNumber(e.target.value))} />, formatPhoneNumber(formData.lawFirmPhone), true)}
        </div>
      </SectionCard>

      {/* Section 5: Injuries & Treatments */}
      <SectionCard title="5. Injuries & Treatments" className="mb-3">
        {renderDetailField("Injuries Description", editing && <textarea className="mcl-form-textarea" value={formData.injuriesDescription || ''} onChange={(e) => updateField('injuriesDescription', e.target.value)} rows={3} />, formData.injuriesDescription, false)}
        {renderDetailField("Started Treatment", editing && <RadioGroup name="hasStartedTreatment" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} value={formData.hasStartedTreatment} onChange={(v) => updateField('hasStartedTreatment', v)} />, getRadioDisplayValue(formData.hasStartedTreatment, [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]), false)}
        {formData.hasStartedTreatment === 'yes' && (
          <>
            {renderDetailField(
              "Where did you go?",
              editing && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {TREATMENT_LOCATIONS.map((option) => (
                    <label key={option.value} className="mcl-checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.treatmentLocations?.includes(option.value) || false}
                        onChange={(e) => {
                          const current = formData.treatmentLocations || [];
                          updateField('treatmentLocations', e.target.checked ? [...current, option.value] : current.filter(v => v !== option.value));
                        }}
                        data-testid={`edit-checkbox-location-${option.value}`}
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              ),
              formatTreatmentLocations(formData.treatmentLocations),
              false
            )}
            {renderDetailField(
              "Treatments Completed",
              editing && <RadioGroup name="treatmentsCompleted" options={TREATMENTS_COMPLETED_OPTIONS} value={formData.treatmentsCompleted} onChange={(v) => updateField('treatmentsCompleted', v)} />,
              getRadioDisplayValue(formData.treatmentsCompleted, TREATMENTS_COMPLETED_OPTIONS),
              false
            )}
            {renderDetailField(
              "Surgical Recommendations",
              editing && <RadioGroup name="surgicalRecommendations" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} value={formData.surgicalRecommendations} onChange={(v) => updateField('surgicalRecommendations', v)} />,
              getRadioDisplayValue(formData.surgicalRecommendations, [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]),
              false
            )}
          </>
        )}
        {renderDetailField("Physical Therapy / Chiropractor", editing && <RadioGroup name="hadPtOrChiro" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'not_sure', label: 'Not Sure' }]} value={formData.hadPtOrChiro} onChange={(v) => updateField('hadPtOrChiro', v)} />, getRadioDisplayValue(formData.hadPtOrChiro, [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'not_sure', label: 'Not Sure' }]), true)}
        {renderDetailField("Serious Injections", editing && <RadioGroup name="hadSeriousInjections" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'not_sure', label: 'Not Sure' }, { value: 'expecting', label: 'Expecting' }]} value={formData.hadSeriousInjections} onChange={(v) => updateField('hadSeriousInjections', v)} />, getRadioDisplayValue(formData.hadSeriousInjections, [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'not_sure', label: 'Not Sure' }, { value: 'expecting', label: 'Expecting' }]), true)}
        {renderDetailField("Broken Bones", editing && <RadioGroup name="brokeBones" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'unknown', label: 'Unknown' }]} value={formData.brokeBones} onChange={(v) => updateField('brokeBones', v)} />, getRadioDisplayValue(formData.brokeBones, [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'unknown', label: 'Unknown' }]), true)}
        {renderDetailField("Surgery", editing && <RadioGroup name="hadSurgery" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'expecting', label: 'Expecting' }]} value={formData.hadSurgery} onChange={(v) => updateField('hadSurgery', v)} />, getRadioDisplayValue(formData.hadSurgery, [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'expecting', label: 'Expecting' }]), true)}
        {renderDetailField("Other Injuries / Treatments", editing && <textarea className="mcl-form-textarea" value={formData.otherInjuriesTreatments || ''} onChange={(e) => updateField('otherInjuriesTreatments', e.target.value)} rows={3} />, formData.otherInjuriesTreatments, false)}
        {renderDetailField("Comments", editing && <textarea className="mcl-form-textarea" value={formData.comments || ''} onChange={(e) => updateField('comments', e.target.value)} rows={3} />, formData.comments, false)}
      </SectionCard>

      {/* Section 6: Additional Information */}
      <SectionCard title="6. Additional Information" className="mb-3">
        {renderDetailField("Child Support Liens", editing && <RadioGroup name="childSupportLiens" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'unknown', label: 'Unknown' }]} value={formData.childSupportLiens} onChange={(v) => updateField('childSupportLiens', v)} />, getRadioDisplayValue(formData.childSupportLiens, [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'unknown', label: 'Unknown' }]), true)}
        {renderDetailField("Currently in Bankruptcy", editing && <RadioGroup name="inBankruptcy" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} value={formData.inBankruptcy} onChange={(v) => updateField('inBankruptcy', v)} />, getRadioDisplayValue(formData.inBankruptcy, [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]), true)}
      </SectionCard>

      {/* Bottom Save/Cancel bar when editing */}
      {isEditingAll && (
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginBottom: '1.25rem' }}>
          <button className="mcl-btn mcl-btn-success" onClick={handleSaveAll} disabled={saving} data-testid="save-all-bottom-button">
            <Save size={16} /> {saving ? 'Saving...' : 'Save All Changes'}
          </button>
          <button className="mcl-btn mcl-btn-outline" onClick={handleCancelClick} data-testid="cancel-all-bottom-button">
            Cancel
          </button>
        </div>
      )}

      {/* Documents */}
      {/* Activity Timeline — full lifecycle including funder doc requests */}
      <ActivityTimelineCard application={formData} offers={offers} documentRequests={documentRequests} />
      {/* Requested Documents (funder asks) — outstanding + fulfilled list with per-row upload */}
      <DocumentRequestsCard applicationId={application.application_id} mode={userRole === 'funder' ? 'view' : 'fulfill'} getAuthHeaders={getAuthHeaders} onChanged={fetchTimelineSources } />
      <SectionCard title="Messages" className="mb-3">
        <MessagesPanel applicationId={application.application_id} currentUser={currentUser} />
      </SectionCard>

      {/* Document uploads remain enabled at any status so clients can submit
          supporting records even after the case is approved/funded. */}
      <DocumentUpload applicationId={application.application_id} canUpload={userRole === 'client' || userRole === 'lawyer'} getAuthHeaders={getAuthHeaders} userRole="client" />

      {/* Internal Notes (lawyer/funder only) */}
      {(userRole === 'lawyer' || userRole === 'funder') && (
        <div className="mcl-card" style={{ marginBottom: '1.25rem', borderColor: 'var(--mcl-warning)' }}>
          <div className="mcl-card-header" style={{ borderBottomColor: '#fde68a' }}>
            <h3 className="mcl-card-header-title" style={{ color: '#92400e' }}>Internal-Lawyer Notes</h3>
            <span style={{ fontSize: '0.75rem', color: '#d97706' }}>Not visible to client</span>
          </div>
          <div className="mcl-card-body">
            <textarea className="mcl-form-textarea" value={formData.internalNotes || ''} onChange={(e) => updateField('internalNotes', e.target.value)} rows={4} placeholder="Add internal notes here..." data-testid="internal-notes" />
            <div style={{ marginTop: '0.75rem' }}>
              <button className="mcl-btn mcl-btn-sm mcl-btn-primary" onClick={async () => {
                try {
                  await apiPut(`/applications/${application.application_id}`, { internalNotes: formData.internalNotes });
                  toast.success('Notes saved');
                } catch (error) { toast.error(error.message || 'Failed to save notes'); }
              }} data-testid="save-internal-notes"><Save size={14} /> Save Notes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientDashboard;
